// Lo que consume un mensaje en un nivel de inteligencia, en los dos selectores (el botón del
// chat y la pastilla de la home). Número: `CREDITOS_APROX_POR_MENSAJE`
// (ADR-2026-10-15-creditos-por-mensaje-en-el-selector-de-inteligencia-2).
//
// Dónde se ve depende del dispositivo
// (ADR-2026-10-15-el-costo-de-la-inteligencia-se-ve-al-pasar-el-mouse):
//  • Sin hover (teléfono, tablet táctil): `CostoPorMensaje`, debajo de la bajada del nivel.
//  • Con mouse: `TarjetaDeCosto` en un `AlCostado`, a la izquierda del panel, mientras el
//    mouse está sobre el nivel.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CREDITOS_APROX_POR_MENSAJE,
  creditosAproxTexto,
  INTELLIGENCE_LABEL,
  type Intelligence,
} from '../../pages/v2/agentModels';
import { IconoCreditos } from '../../pages/flujos/panels/PillCreditos';

const esGratis = (nivel: Intelligence) => CREDITOS_APROX_POR_MENSAJE[nivel] === 0;

/** "Gratis" o "🪙 1,28 aprox. por mensaje", en una línea. */
export const CostoPorMensaje: React.FC<{ nivel: Intelligence; className?: string }> = ({
  nivel,
  className = '',
}) => {
  if (esGratis(nivel)) return <span className={className}>Gratis</span>;
  return (
    <span className={`inline-flex items-center gap-1 tabular-nums ${className}`}>
      <IconoCreditos className="h-3.5 w-3.5" />
      {creditosAproxTexto(nivel)}
      {/* El ícono no se lee: el lector de pantalla oye "créditos" igual. */}
      <span className="sr-only">créditos</span> aprox. por mensaje
    </span>
  );
};

/** El ancho de la tarjeta del costo y el aire contra el panel y la pantalla. */
const ANCHO_TARJETA = 232;
const SEPARACION = 8;
const MARGEN = 8;

/** La tarjeta del costo: el número grande con el ícono, y qué significa. */
export const TarjetaDeCosto: React.FC<{ nivel: Intelligence }> = ({ nivel }) => {
  const gratis = esGratis(nivel);
  return (
    // Radios concéntricos: 12 adentro + 4 de padding = 16 afuera.
    <div className="rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.06)] bg-white p-1 shadow-[0_8px_30px_rgba(16,24,45,0.14)]">
      <div className="flex items-center justify-center gap-1.5 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.095)] px-3 py-4">
        {gratis ? (
          <span className="text-[22px] font-semibold text-brand-primary">Gratis</span>
        ) : (
          <>
            <IconoCreditos className="h-5 w-5" />
            <span className="text-[22px] font-semibold tabular-nums text-brand-primary">
              {creditosAproxTexto(nivel)}
            </span>
            <span className="text-[13px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.5)]">créditos</span>
          </>
        )}
      </div>
      <div className="px-3 pb-2.5 pt-2.5">
        <p className="text-[13.5px] font-semibold text-brand-black">
          {gratis ? 'Sin costo por mensaje' : 'Costo aprox. por mensaje'}
        </p>
        <p className="mt-1 text-[12px] leading-snug text-[rgb(var(--brand-black-rgb)_/_0.55)]">
          {gratis
            ? 'Conversar con el asistente no consume créditos. Lo que genere (fotos, videos) se cobra aparte.'
            : `Lo que usa un mensaje promedio en Inteligencia ${INTELLIGENCE_LABEL[nivel]}. Las conversaciones largas usan más.`}
        </p>
      </div>
    </div>
  );
};

/**
 * Pone su contenido FLOTANDO al costado de `ancla` (el panel): a la izquierda, y a la derecha
 * si a la izquierda no entra. En altura, a la de `fila` —la opción que se está mirando—, sin
 * salirse de la pantalla; al pasar de una fila a otra se desliza. Va por portal y en `fixed`
 * porque los paneles tienen scroll propio (`overflow-y-auto`) y algo absoluto adentro
 * quedaría recortado. No recibe clicks: es información, y así no cuenta como "click afuera".
 */
export const AlCostado: React.FC<{
  ancla: HTMLElement | null;
  fila: HTMLElement | null;
  children: React.ReactNode;
}> = ({ ancla, fila, children }) => {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const tarjetaRef = useRef<HTMLDivElement | null>(null);
  // El deslizamiento entre filas se prende DESPUÉS de la primera ubicación: si no, la
  // tarjeta entraría deslizándose desde el borde de arriba de la pantalla.
  const [deslizar, setDeslizar] = useState(false);
  useEffect(() => {
    if (!pos || deslizar) return;
    const id = requestAnimationFrame(() => setDeslizar(true));
    return () => cancelAnimationFrame(id);
  }, [pos, deslizar]);

  useLayoutEffect(() => {
    if (!ancla) return;
    const medir = () => {
      const r = ancla.getBoundingClientRect();
      let left = r.left - SEPARACION - ANCHO_TARJETA;
      if (left < MARGEN) left = Math.min(r.right + SEPARACION, window.innerWidth - ANCHO_TARJETA - MARGEN);
      // El borde de arriba de la tarjeta, al de la fila: con el padding de la tarjeta (4 px)
      // el bloque azul queda parejo con la fila. Si abajo no entra, sube lo justo.
      const alto = tarjetaRef.current?.offsetHeight ?? 0;
      const deseado = (fila ?? ancla).getBoundingClientRect().top - 4;
      const top = Math.max(MARGEN, Math.min(deseado, window.innerHeight - alto - MARGEN));
      setPos({ left, top });
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [ancla, fila]);

  if (!ancla) return null;
  return createPortal(
    <div
      ref={tarjetaRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        width: ANCHO_TARJETA,
        // La primera medición necesita el alto ya pintado: hasta entonces, invisible.
        visibility: pos ? 'visible' : 'hidden',
      }}
      className={`pointer-events-none z-[121] animate-fade-in ${
        deslizar ? 'transition-[top] duration-150 ease-out' : ''
      }`}
    >
      {children}
    </div>,
    document.body,
  );
};

export default CostoPorMensaje;
